import React, { useState } from 'react';
import Plot from 'react-plotly.js';

// Local formatting helpers (mirror optimizations/OptimizationsLayout.js).
// All percentage metrics here arrive already expressed in percent
// (e.g. total_return = 23.5 means +23.5%), matching how the backtest
// service, the strategy BacktestResult, the optimization combos and the
// regenerated portfolio track record all store them.
const fmtNum = (n, d = 2) => (n == null || isNaN(n) ? '—' : Number(n).toFixed(d));
const fmtPct = (n, d = 1) => (n == null || isNaN(n) ? '—' : `${n >= 0 ? '+' : ''}${Number(n).toFixed(d)}%`);
const valColor = (n) => (n == null || isNaN(n) ? 'text-gray-400' : n > 0 ? 'text-green-400' : n < 0 ? 'text-red-400' : 'text-gray-400');

const MetricTile = ({ label, value, isPct, isRaw, neutral }) => {
  const display = value == null ? '—'
    : isPct ? fmtPct(value)
    : isRaw ? String(value)
    : fmtNum(value);
  const color = neutral ? 'text-gray-700 dark:text-gray-200' : (value != null ? valColor(value) : 'text-gray-400');
  return (
    <div className="bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2">
      <div className="text-[10px] text-gray-400 mb-0.5 truncate" title={label}>{label}</div>
      <div className={`text-sm font-semibold tabular-nums ${color}`}>{display}</div>
    </div>
  );
};

// Lazy-mounted equity chart. Takes an absolute-equity primary curve and an
// optional benchmark curve (already on the same base). No network — both
// curves are supplied by the caller. Draws an optional OOS split marker.
const EquityChart = ({ curve, benchCurve, benchLabel, oosSplit }) => {
  const shapes = [];
  if (oosSplit) {
    shapes.push({
      type: 'line', xref: 'x', yref: 'paper',
      x0: oosSplit, x1: oosSplit, y0: 0, y1: 1,
      line: { color: '#3b82f6', width: 1, dash: 'dot' },
    });
  }
  return (
    <Plot
      data={[
        {
          x: curve.map(p => p.date),
          y: curve.map(p => p.equity),
          type: 'scatter', mode: 'lines',
          name: 'Strategy',
          line: { color: '#c9a96e', width: 1.5 },
          fill: 'tozeroy',
          fillcolor: 'rgba(201,169,110,0.08)',
          hovertemplate: '$%{y:,.0f}<extra>Strategy</extra>',
        },
        ...(benchCurve && benchCurve.length > 0 ? [{
          x: benchCurve.map(p => p.date),
          y: benchCurve.map(p => p.equity),
          type: 'scatter', mode: 'lines',
          name: benchLabel || 'Buy & Hold',
          line: { color: '#ef4444', width: 1, dash: 'dot' },
          hovertemplate: `$%{y:,.0f}<extra>${benchLabel || 'B&H'}</extra>`,
        }] : []),
      ]}
      layout={{
        height: 240,
        margin: { t: 10, b: 30, l: 55, r: 10 },
        paper_bgcolor: 'transparent',
        plot_bgcolor: 'transparent',
        showlegend: benchCurve && benchCurve.length > 0,
        legend: { orientation: 'h', y: 1.12, x: 0, font: { size: 9, color: '#9ca3af' } },
        shapes,
        xaxis: { gridcolor: 'rgba(128,128,128,0.12)', tickfont: { size: 9, color: '#9ca3af' } },
        yaxis: { gridcolor: 'rgba(128,128,128,0.12)', tickfont: { size: 9, color: '#9ca3af' }, tickprefix: '$' },
        font: { color: '#9ca3af' },
      }}
      config={{ displayModeBar: false, responsive: true }}
      style={{ width: '100%' }}
      useResizeHandler
    />
  );
};

/**
 * Full backtest performance panel — surfaces the same depth as the
 * optimization combo detail, sourced WITHOUT any backend recompute from up to
 * three places, any of which may be present:
 *
 *   bt      stored BacktestResult (basic metrics + results_data.metrics +
 *           stored equity curve). Usually absent for deploy-from-combo
 *           strategies (deploy_from_combo writes no BacktestResult).
 *   member  regenerated portfolio TRACK RECORD for this strategy
 *           ({member: metrics, equity_curve, spx_curve, oos_split, window}).
 *           This is the 2016→today backtest run by regenerate_portfolio_curves.
 *   combo   source optimization combo (blended IS + OOS metrics).
 *   runMeta the source run's B&H benchmarks (bh_* incl. idle/SPY bh_idle_*).
 *
 * Renders whatever is available; only shows the empty-state when ALL are null.
 */
const BacktestMetricsPanel = ({ bt, combo, runMeta, member, idleAsset }) => {
  const [showBtChart, setShowBtChart] = useState(false);
  const [showMemberChart, setShowMemberChart] = useState(false);

  const mm = member && member.member ? member.member : null;
  const hasData = bt || combo || mm;

  if (!hasData) {
    return (
      <p className="text-[11px] text-gray-500 italic">
        No backtest, track record, or optimization combo on record for this strategy yet.
      </p>
    );
  }

  // ── Stored BacktestResult (when present) ──────────────────────────────────
  const m = (bt && bt.results_data && bt.results_data.metrics) || {};
  const ec = (bt && (bt.equity_curve || (bt.results_data && bt.results_data.equity_curve))) || [];
  const priceSeries = (bt && bt.results_data && bt.results_data.price_series) || [];

  // Buy & hold overlay for the stored-bt chart: normalize the price series to
  // the equity curve's starting capital so the two lines share a common base.
  let bhCurve = [];
  if (showBtChart && ec.length > 0 && priceSeries.length > 1) {
    const base = ec[0]?.equity;
    const px0 = priceSeries[0]?.close;
    if (base && px0) {
      bhCurve = priceSeries.map(p => ({ date: p.date, equity: base * (p.close / px0) }));
    }
  }

  const idleLabel = idleAsset ? `B&H ${idleAsset}` : null;

  // ── Member track-record curve range (for the subtitle) ────────────────────
  const memberCurve = (member && member.equity_curve) || [];
  const memberSpx = (member && member.spx_curve) || [];
  const trackRange = memberCurve.length > 1
    ? `${memberCurve[0].date} → ${memberCurve[memberCurve.length - 1].date}`
    : (member && member.window) || null;

  return (
    <div className="space-y-5">
      {/* ── Strategy Track Record (regenerated portfolio backtest) ─────────── */}
      {mm && (
        <div>
          <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">
            Strategy Track Record
            {trackRange ? (
              <span className="ml-1.5 text-gray-500 normal-case tracking-normal">{trackRange}</span>
            ) : null}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            <MetricTile label="Total Return"    value={mm.total_return}      isPct />
            <MetricTile label="Ann. Return"     value={mm.annual_return}     isPct />
            <MetricTile label="Sharpe"          value={mm.sharpe_ratio} />
            <MetricTile label="Calmar"          value={mm.calmar_ratio} />
            <MetricTile label="Max Drawdown"    value={mm.max_drawdown}      isPct />
            <MetricTile label="Ann. Volatility" value={mm.annual_volatility} isPct neutral />
            <MetricTile label="Win Rate"        value={mm.win_rate}          isPct neutral />
            <MetricTile label="Profit Factor"   value={mm.profit_factor} />
            <MetricTile label="Num Trades"      value={mm.num_trades}        isRaw neutral />
          </div>
          {mm.error && (
            <div className="text-[10px] text-amber-500 mt-1.5">
              Track record incomplete: {mm.error}
            </div>
          )}
          {/* Track-record equity curve vs SPX (Buy & Hold) */}
          {memberCurve.length > 0 && (
            <div className="mt-3">
              {!showMemberChart ? (
                <button
                  onClick={() => setShowMemberChart(true)}
                  className="text-[11px] border border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10 px-3 py-1.5 rounded-lg transition-colors"
                >
                  Show track-record chart vs SPX
                </button>
              ) : (
                <EquityChart
                  curve={memberCurve}
                  benchCurve={memberSpx}
                  benchLabel="Buy & Hold SPY"
                  oosSplit={member && member.oos_split}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Stored backtest (only when a BacktestResult exists) ────────────── */}
      {bt && (
        <div>
          <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Strategy Performance</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            <MetricTile label="Total Return"   value={bt.total_return}      isPct />
            <MetricTile label="Ann. Return"    value={m.annual_return}      isPct />
            <MetricTile label="Sharpe"         value={bt.sharpe_ratio} />
            <MetricTile label="Calmar"         value={m.calmar_ratio} />
            <MetricTile label="Max Drawdown"   value={bt.max_drawdown}      isPct />
            <MetricTile label="Ann. Volatility" value={m.annual_volatility} isPct neutral />
            <MetricTile label="Win Rate"       value={bt.win_rate}          isPct neutral />
            <MetricTile label="Profit Factor"  value={bt.profit_factor} />
            <MetricTile label="Num Trades"     value={bt.num_trades}        isRaw neutral />
            <MetricTile label="Alpha (ann.)"   value={m.alpha}              isPct />
          </div>
          {(bt.symbol || bt.start_date) && (
            <div className="text-[10px] text-gray-400 mt-1.5">
              {bt.symbol}{bt.start_date ? ` · ${bt.start_date} → ${bt.end_date}` : ''}
            </div>
          )}
        </div>
      )}

      {/* ── Benchmark · Buy & Hold ─────────────────────────────────────────── */}
      {/* From stored bt (preferred) or the source optimization's run_meta. */}
      {(bt || runMeta) && (
        <div>
          <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Benchmark · Buy &amp; Hold</h3>
          {bt ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                <MetricTile label="B&H Return"      value={m.buy_hold_return}     isPct />
                <MetricTile label="B&H Ann. Return" value={m.buy_hold_ann_return} isPct />
                <MetricTile label="B&H Sharpe"      value={m.buy_hold_sharpe} />
                {m.buy_hold_max_dd != null && <MetricTile label="B&H Max DD" value={m.buy_hold_max_dd} isPct />}
                {idleLabel && m.idle_buy_hold != null && (
                  <MetricTile label={`${idleLabel} Return`} value={m.idle_buy_hold} isPct />
                )}
                {idleLabel && m.idle_buy_hold_cagr != null && (
                  <MetricTile label={`${idleLabel} CAGR`} value={m.idle_buy_hold_cagr} isPct />
                )}
              </div>
              {m.alpha != null && (
                <div className="text-[10px] text-gray-400 mt-1.5">
                  Alpha = strategy ann. return − B&amp;H ann. return.
                </div>
              )}
            </>
          ) : (
            (() => {
              // The combo metrics rendered below are IS-window ("In-Sample
              // (Blended)"), so the legacy runMeta.bh_* scalars — which are
              // computed IS-ONLY for any run with an OOS split (base_data is
              // trimmed to is_end_date before finalize) — are the CORRECT
              // partner. Only the window LABEL was lying: it claimed the full
              // start→end window even when B&H stopped at is_end_date.
              const isOos = runMeta.oos_split_pct > 0 && !!runMeta.is_end_date;
              const winEnd = isOos ? runMeta.is_end_date : runMeta.end_date;
              const bhSfx = isOos ? ', IS' : '';
              return (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    <MetricTile label={`B&H Return${runMeta.symbol ? ` (${runMeta.symbol}${bhSfx})` : bhSfx}`} value={runMeta.bh_total_return} isPct />
                    <MetricTile label={`B&H Sharpe${bhSfx}`}  value={runMeta.bh_sharpe_ratio} />
                    {runMeta.bh_max_drawdown != null && <MetricTile label={`B&H Max DD${bhSfx}`} value={runMeta.bh_max_drawdown} isPct />}
                    {runMeta.idle_deployment && runMeta.idle_deployment !== 'cash' && runMeta.bh_idle_total_return != null && (
                      <MetricTile label={`B&H ${runMeta.idle_deployment} Return${bhSfx}`} value={runMeta.bh_idle_total_return} isPct />
                    )}
                    {runMeta.idle_deployment && runMeta.idle_deployment !== 'cash' && runMeta.bh_idle_sharpe_ratio != null && (
                      <MetricTile label={`B&H ${runMeta.idle_deployment} Sharpe${bhSfx}`} value={runMeta.bh_idle_sharpe_ratio} />
                    )}
                  </div>
                  <div className="text-[10px] text-gray-400 mt-1.5">
                    Benchmark over the {isOos ? 'in-sample' : 'optimization'} window
                    {runMeta.start_date ? ` (${runMeta.start_date} → ${winEnd})` : ''}.
                  </div>
                </>
              );
            })()
          )}
        </div>
      )}

      {/* ── Optimization combo — blended (robustness-adjusted) IS metrics ──── */}
      {combo && (
        <div>
          <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">
            Optimization · In-Sample (Blended)
            {combo._combo_index != null || combo.combo_index != null ? (
              <span className="ml-1.5 text-gray-500 normal-case tracking-normal">combo #{combo._combo_index ?? combo.combo_index}</span>
            ) : null}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            <MetricTile label="Blended Sharpe" value={combo.blended_sharpe} />
            <MetricTile label="Blended Return" value={combo.blended_return} isPct />
            <MetricTile label="Sortino"        value={combo.sortino_ratio} />
            {combo.blended_sortino != null && <MetricTile label="Blend Sortino" value={combo.blended_sortino} />}
            {combo.blended_calmar  != null && <MetricTile label="Blend Calmar"  value={combo.blended_calmar} />}
            {combo.nsr_score       != null && <MetricTile label="NSR Score"     value={combo.nsr_score} />}
          </div>
        </div>
      )}

      {/* ── Optimization combo — out-of-sample validation ──────────────────── */}
      {combo && combo.oos_blended_sharpe != null && (
        <div>
          <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Optimization · Out-of-Sample</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            <MetricTile label="OOS Blend Sharpe"  value={combo.oos_blended_sharpe} />
            <MetricTile label="OOS Sharpe"        value={combo.oos_sharpe_ratio} />
            <MetricTile label="Degradation Ratio" value={combo.oos_degradation_ratio} />
            <MetricTile label="OOS Total Return"  value={combo.oos_total_return} isPct />
            <MetricTile label="OOS Max Drawdown"  value={combo.oos_max_drawdown} isPct />
            <MetricTile label="OOS Trades"        value={combo.oos_num_trades} isRaw neutral />
          </div>
        </div>
      )}

      {/* ── Stored equity curve — mounted on demand (no network) ───────────── */}
      {bt && (
        <div>
          <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Equity Curve</h3>
          {ec.length === 0 ? (
            <p className="text-[11px] text-gray-500 italic">No stored equity curve.</p>
          ) : !showBtChart ? (
            <button
              onClick={() => setShowBtChart(true)}
              className="text-[11px] border border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10 px-3 py-1.5 rounded-lg transition-colors"
            >
              Show equity chart
            </button>
          ) : (
            <EquityChart curve={ec} benchCurve={bhCurve} benchLabel="Buy & Hold" />
          )}
        </div>
      )}
    </div>
  );
};

export default BacktestMetricsPanel;
